import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { crmService, Client, TenantUserOption } from '../../services/crmService';
import { essentialsService } from '../../services/essentialsService';
import { ListPageHeader } from '../crm/ListPageHeader';
import { ListToolbar } from '../crm/ListToolbar';
import { DataListTable, Column } from '../crm/DataListTable';
import { CrmEmptyState } from '../crm/EmptyState';
import { formatPhoneDisplay } from '../../utils/phoneFormat';
import { leadSourceBadgeClass } from '../../utils/statusStyles';
import ContactDetailPanel from './ContactDetailPanel';
import { ListStage, STAGE_META, qualificationSummary } from './stageConfig';

interface ContactsListPageProps {
  /** The lifecycle stage this route renders. One component, three routes. */
  stage: ListStage;
}

const SORT_OPTIONS = [
  { value: '-updated_at', label: 'Recently updated' },
  { value: 'next_follow_up_at', label: 'Follow-up date' },
];

function relativeTime(iso?: string | null): string {
  if (!iso) return '—';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 60) return `${Math.max(min, 0)}m ago`;
  if (min < 1440) return `${Math.round(min / 60)}h ago`;
  const day = Math.round(min / 1440);
  return day < 30 ? `${day}d ago` : new Date(iso).toLocaleDateString();
}

function followUpBadge(c: Client): React.ReactNode {
  if (!c.next_follow_up_at) return null;
  const due = new Date(c.next_follow_up_at).getTime();
  const now = Date.now();
  if (due < now) {
    return <span className="rounded-full bg-danger-fg/10 px-2 py-0.5 text-xs font-semibold text-danger-fg">Overdue</span>;
  }
  if (due < now + 24 * 3600 * 1000) {
    return <span className="rounded-full bg-warning-fg/10 px-2 py-0.5 text-xs font-semibold text-warning-fg">Due soon</span>;
  }
  return null;
}

const ContactsListPage: React.FC<ContactsListPageProps> = ({ stage }) => {
  const meta = STAGE_META[stage];
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<Client[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [ordering, setOrdering] = useState('-updated_at');
  const [assignedTo, setAssignedTo] = useState('');
  const [source, setSource] = useState('');
  const [followUpDue, setFollowUpDue] = useState(searchParams.get('followup') === 'due');
  const [users, setUsers] = useState<TenantUserOption[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);

  // 300ms debounce on server-side search.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  useEffect(() => {
    crmService.getTenantUsers().then(setUsers).catch(() => setUsers([]));
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params: Parameters<typeof crmService.getClients>[0] = {
        stage,
        ordering,
        page: String(page),
      };
      if (search) params.search = search;
      if (assignedTo) params.assigned_to = assignedTo;
      if (source) params.source = source;
      if (followUpDue) params.follow_up = 'today';
      const res = await crmService.getClients(params);
      setRows(res.results);
      setCount(res.count ?? res.results.length);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [stage, ordering, page, search, assignedTo, source, followUpDue]);

  useEffect(() => {
    load();
  }, [load]);

  const sources = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => r.source && s.add(r.source));
    return Array.from(s).sort();
  }, [rows]);

  const handleExport = async () => {
    try {
      const blob = await essentialsService.exportCsv(stage, {
        search: search || undefined,
        source: source || undefined,
        assigned_to: assignedTo || undefined,
        follow_up: followUpDue ? 'due' : undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${meta.pluralLabel.toLowerCase()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Export failed');
    }
  };

  const columns: Column<Client>[] = [
    {
      id: 'name',
      header: 'Name',
      mobilePriority: 'primary',
      render: (c) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-fg">{c.full_name}</div>
          {c.company_name ? <div className="truncate text-xs text-fg-muted">{c.company_name}</div> : null}
          <div className="text-xs text-fg-muted">{formatPhoneDisplay(c.phone) || c.email}</div>
        </div>
      ),
    },
    {
      id: 'summary',
      header: stage === 'client' ? 'Details' : 'Facility / qualification',
      mobileLabel: 'Details',
      render: (c) => <span className="text-sm text-fg-muted">{qualificationSummary(c) || '—'}</span>,
    },
    {
      id: 'source',
      header: 'Source',
      mobileLabel: 'Source',
      render: (c) => (c.source ? <span className={leadSourceBadgeClass(c.source)}>{c.source}</span> : <span className="text-fg-muted">—</span>),
    },
    {
      id: 'assigned',
      header: 'Assigned',
      mobileLabel: 'Assigned',
      render: (c) => <span className="text-sm text-fg">{c.assigned_to_name || '—'}</span>,
    },
    {
      id: 'updated',
      header: 'Updated',
      mobileLabel: 'Updated',
      render: (c) => <span className="text-sm text-fg-muted">{relativeTime(c.updated_at)}</span>,
    },
    {
      id: 'followup',
      header: 'Follow-up',
      mobileLabel: 'Follow-up',
      render: (c) => followUpBadge(c) ?? <span className="text-fg-muted">—</span>,
    },
  ];

  const PAGE_SIZE = 20;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <ListPageHeader
        title={meta.pluralLabel}
        subtitle={`Your ${meta.pluralLabel.toLowerCase()} — search, filter, and follow up.`}
        actions={
          <button type="button" onClick={handleExport} className="btn-secondary rounded-xl px-4 py-2 text-sm font-medium">
            Export CSV
          </button>
        }
      />

      <ListToolbar
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search name, company, email, phone…"
        sortValue={ordering}
        sortOptions={SORT_OPTIONS}
        onSortChange={(v) => {
          setOrdering(v);
          setPage(1);
        }}
        showClear={!!(search || assignedTo || source || followUpDue)}
        onClearFilters={() => {
          setSearchInput('');
          setAssignedTo('');
          setSource('');
          setFollowUpDue(false);
          setPage(1);
        }}
        quickFilter={
          <label className="inline-flex items-center gap-2 text-sm text-fg">
            <input type="checkbox" checked={followUpDue} onChange={(e) => { setFollowUpDue(e.target.checked); setPage(1); }} />
            Follow-up due
          </label>
        }
        advancedFilters={
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <select
              value={assignedTo}
              onChange={(e) => { setAssignedTo(e.target.value); setPage(1); }}
              className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
            >
              <option value="">All assignees</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</option>
              ))}
            </select>
            <select
              value={source}
              onChange={(e) => { setSource(e.target.value); setPage(1); }}
              className="input-field rounded-xl border border-border bg-input px-3 py-2 text-sm text-fg"
            >
              <option value="">All sources</option>
              {sources.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        }
        advancedFilterCount={(assignedTo ? 1 : 0) + (source ? 1 : 0)}
      />

      {error ? <div className="alert-danger rounded-xl px-4 py-3">{error}</div> : null}

      <DataListTable<Client>
        columns={columns}
        rows={rows}
        getRowKey={(c) => c.id}
        onRowClick={(c) => setOpenId(c.id)}
        loading={loading}
        emptyContent={
          <CrmEmptyState
            title={`No ${meta.pluralLabel.toLowerCase()} yet`}
            description={
              stage === 'lead'
                ? 'Your website form and chatbot are live and connected — new leads will appear here.'
                : `Contacts you move to ${meta.pluralLabel.toLowerCase()} will appear here.`
            }
          />
        }
      />

      {count > PAGE_SIZE ? (
        <div className="flex items-center justify-between text-sm text-fg-muted">
          <span>
            Page {page} of {totalPages} · {count} total
          </span>
          <div className="flex gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="btn-secondary rounded-lg px-3 py-1.5 disabled:opacity-50">
              Previous
            </button>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="btn-secondary rounded-lg px-3 py-1.5 disabled:opacity-50">
              Next
            </button>
          </div>
        </div>
      ) : null}

      {openId ? (
        <ContactDetailPanel
          contactId={openId}
          users={users}
          onClose={() => setOpenId(null)}
          onChanged={() => load()}
        />
      ) : null}
    </div>
  );
};

export default ContactsListPage;
